import { CPENZLoader } from "@/components/CPENZLoader";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, Timer, Sparkles, TrendingUp, TrendingDown, Loader2, CalendarDays, Trash2, Lock, Unlock, Star, FlaskConical, Clock } from "lucide-react";
import { PassportButton } from "@/components/IdeaPassportCard";
import { MaturityTag } from "@/components/MaturityTag";
import Navbar from "@/components/Navbar";
import { AppNav } from "@/components/AppNav";
import { CPENZIcon } from "@/components/CPENZIcon";
import { ScoreRadial, bandColor, useBandLabel } from "@/components/ScoreRadial";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { shareToInstagram } from "@/lib/shareImage";
import ShareCardDialog from "@/components/ShareCardDialog";
import { NextMoveCoach } from "@/components/NextMoveCoach";
import { EvidencePanel } from "@/components/EvidencePanel";
import { IdeaMap } from "@/components/IdeaMap";
import { Timeline } from "@/components/Timeline";
import { IdeaConnections } from "@/components/IdeaConnections";
import { RenameIdea } from "@/components/RenameIdea";
import { SectionDock } from "@/components/SectionSwitcher";
import IdeaPassport from "@/components/ideaos/IdeaPassport";
import { IdeaCover } from "@/components/ideaos/shared";
import { CoverEditor } from "@/components/CoverEditor";
import { IdeaPitch } from "@/components/ideaos/IdeaPitch";
import { CreateWithCpenz } from "@/components/CreateWithCpenz";
import { L } from "@/components/ideaos/shared";
import { IdeaDocuments } from "@/components/ideaos/IdeaDocuments";
import { IdeaAccesses } from "@/components/IdeaAccesses";
import { PlanCalendar } from "@/components/PlanCalendar";
import { DecisionPanel } from "@/components/DecisionPanel";
import { PublishButton } from "@/components/PublishDialog";
import { useAuth } from "@/context/AuthContext";
const IdeaCanvas = React.lazy(() => import("@/components/canvas/IdeaCanvas"));
const IdeaReport = React.lazy(() => import("@/components/IdeaReport").then((m) => ({ default: m.IdeaReport })));
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/i18n/LanguageContext";
import { useAuthDialog } from "@/context/AuthDialogContext";
import api, { formatApiError } from "@/lib/api";
import { STEPS } from "@/data/steps";
import { toast } from "sonner";

const STATUS_COLORS = {
  DISCOVERING: "#3b82f6", STRUCTURING: "#7c3aed", VALIDATING: "#0ea5e9",
  "READY TO TEST": "#14b8a6", "READY TO BUILD": "#16a34a", "ON HOLD": "#94a3b8",
  REWORK: "#f97316", COMPLETED: "#16a34a",
};

function List({ items, color = "#7c3aed" }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-2 mt-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2 text-slate-600"><Sparkles size={14} style={{ color }} className="mt-1 shrink-0" /><span>{it}</span></li>
      ))}
    </ul>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const bandLabel = useBandLabel();
  const { user } = useAuth();
  const authDialog = useAuthDialog();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const SUB_PARENT = { record: "passport", story: "history", canvas: "board" };
  const TAB_KEYS = ["overview", "evidence", "plan", "analysis", "score", "passport", "accesses", "tools", "history", "next"];
  const TOOL_TABS = ["create"];
  const SOON = ["compare", "board"];
  const TOOLS = [
    { key: "create", label: L(lang, "Crea con cpenz", "Create with cpenz"), desc: L(lang, "Immagini con prompt catturato: provenienza livello 5", "Images with captured prompt: level-5 provenance"), badge: "new" },
    { key: "board", label: t("detail.tabs.board"), desc: t("detail.tab_desc.board"), soon: true },
    { key: "compare", label: t("detail.tabs.compare"), desc: t("detail.tab_desc.compare"), soon: true },
    { key: "generator", label: t("nav.generator"), desc: t("explorePage.generator_d"), to: "/generatore", badge: "new" },
  ];
  const [tab, setTabState] = useState(() => { const q = new URLSearchParams(window.location.search).get("tab"); const k = SUB_PARENT[q] || q; return k && (TAB_KEYS.includes(k) || TOOL_TABS.includes(k)) && !SOON.includes(k) ? k : "overview"; });
  const setTab = (k) => { if (SOON.includes(k)) return; setTabState(k); window.scrollTo({ top: 0, left: 0, behavior: "instant" }); requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })); };
  const TAB_DEFS = TAB_KEYS.map((k) => [k, t(`detail.tabs.${k}`)]);
  const [evidencePreset, setEvidencePreset] = useState(null);
  const [decision, setDecision] = useState(null);
  const openEvidence = (area, type = "interview") => { setEvidencePreset({ area: area || "customer", type, _k: Date.now() }); setTab("evidence"); };
  const startMove = async () => {
    try { await api.post(`/projects/${id}/next-move/start`); toast.success(t("map.move_started")); await load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const daysIdle = project?.last_activity_at || project?.updated_at ? Math.floor((Date.now() - new Date(project.last_activity_at || project.updated_at)) / 86400000) : 0;

  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledge, setKnowledge] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [impact, setImpact] = useState(null);

  const [taskOpen, setTaskOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDate, setTaskDate] = useState("");

  const [learnOpen, setLearnOpen] = useState(false);
  const [learnTask, setLearnTask] = useState(null);
  const [learnText, setLearnText] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeOutcome, setCloseOutcome] = useState("positive");
  const [closeText, setCloseText] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeResult, setCloseResult] = useState(null);
  const toggleStep = async (i) => {
    const cur = project?.next_move?.checklist_done || [];
    const done = cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i];
    setProject((p) => ({ ...p, next_move: { ...p.next_move, checklist_done: done } }));
    try { await api.post(`/projects/${id}/next-move/checklist`, { done }); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const closeMove = async () => {
    if (closeText.trim().length < 5) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/projects/${id}/next-move/close`, { outcome: closeOutcome, learned: closeText, lang });
      setProject(data.project);
      setCloseResult({ ...data.score_change, area: data.evidence?.area, new_move: data.project?.next_move?.title });
      setCloseOpen(false); setCloseText("");
      toast.success(t("toast.score_up"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setClosing(false); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}?lang=${lang}`);
    setProject(data);
    const tk = await api.get(`/projects/${id}/tasks`);
    setTasks(tk.data);
  }, [id, lang]);

  useEffect(() => { load().catch(() => navigate("/app/dashboard")); }, [load, navigate]);

  const submitKnowledge = async () => {
    if (!knowledge.trim()) return;
    setAnalyzing(true);
    setImpact(null);
    try {
      const { data } = await api.post(`/projects/${id}/knowledge`, { info: knowledge, lang });
      setProject(data.project);
      setImpact(data.result);
      setKnowledge("");
      toast.success(t("toast.added"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const addTask = async (presetTitle) => {
    const title = presetTitle || taskTitle;
    if (!title.trim()) return;
    await api.post(`/projects/${id}/tasks`, { title, date: taskDate || project?.next_move?.deadline || null });
    setTaskOpen(false); setTaskTitle(""); setTaskDate("");
    const tk = await api.get(`/projects/${id}/tasks`);
    setTasks(tk.data);
    toast.success(t("toast.saved"));
  };

  const openComplete = (task) => { setLearnTask(task); setLearnText(""); setLearnOpen(true); };

  const submitLearned = async () => {
    if (!learnTask) return;
    await api.patch(`/tasks/${learnTask.id}`, { status: "COMPLETED", learned: learnText, lang });
    setLearnOpen(false);
    toast.success(t("toast.complete"));
    if (learnText.trim()) {
      setAnalyzing(true);
      try {
        const { data } = await api.post(`/projects/${id}/knowledge`, { info: learnText, lang });
        setProject(data.project);
        setImpact(data.result);
        setTab("score");
        toast.success(t("toast.score_up"));
      } catch (e) { /* ignore */ }
      setAnalyzing(false);
    }
    const tk = await api.get(`/projects/${id}/tasks`);
    setTasks(tk.data);
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks((prev) => prev.filter((x) => x.id !== taskId));
      toast.success(t("toast.task_deleted"));
    } catch (e) { toast.error("—"); }
  };

  const reschedule = async (task) => {
    const d = prompt(t("detail.reschedule"), task.date || "");
    if (d != null) {
      await api.patch(`/tasks/${task.id}`, { date: d });
      const tk = await api.get(`/projects/${id}/tasks`);
      setTasks(tk.data);
    }
  };

  if (!project) return <div className="min-h-screen bg-white flex items-center justify-center"><CPENZLoader size={96} /></div>;

  const score = project.score || {};
  const isOwner = !!user && project.user_id === user.user_id;
  const canEdit = !!user && (project.user_id === user.user_id || (project.collaborators || []).some((c) => c.user_id === user.user_id && c.role === "editor"));
  const fullHeader = tab === "overview";
  const analysis = project.analysis || {};
  const nm = project.next_move || {};
  const history = project.score_history || [];
  const versions = [...(project.versions || [])].reverse();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden pb-24 md:pb-0">
      <Navbar onStart={() => navigate("/app/new")} onLogin={() => authDialog.openAuth()} />
      <AppNav />
      <div className={`mx-auto pt-2 md:pt-3 ${tab === "board" ? "max-w-none px-2 pb-6 sm:px-3" : "max-w-6xl px-5 lg:px-8 pb-20"}`}>
        {fullHeader ? (<>
        {/* COVER + HEADER (overview only) */}
        <div className="relative -mx-5 sm:mx-0" data-testid="detail-hero">
          <IdeaCover id={project.id} url={project.cover_url} name={project.name} className="h-28 sm:h-36 sm:rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f1a]/40 via-transparent to-[#0b0f1a]/10" />
          </IdeaCover>
          <div className="absolute right-3 top-3 flex items-center gap-2" data-testid="detail-hero-actions">{isOwner && <PublishButton p={project} onVisibility={(v) => setProject((x) => ({ ...x, visibility: v }))} onCover={(c) => setProject((x) => ({ ...x, cover_url: c }))} onRenamed={(name) => setProject((x) => ({ ...x, name }))} />}<PassportButton project={project} /></div>
          <div className="absolute bottom-2 right-3" data-testid="detail-cover-editor"><CoverEditor projectId={project.id} coverUrl={project.cover_url} coverAuto={!!project.cover_auto} compact onChanged={(c, auto) => setProject((p) => ({ ...p, cover_url: c, cover_auto: !!auto }))} /></div>
          <div className="absolute -bottom-6 left-4 sm:left-5 rounded-full bg-white p-0.5 shadow-md ring-2 ring-white" data-testid="detail-score-avatar">
            {score.total != null ? <ScoreRadial total={score.total} band={score.band} size={64} animate={false} showBand={false} /> : <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#f4f6fa] px-1.5 text-center text-[9px] font-semibold leading-tight text-slate-400">{t("vault.no_score")}</span>}
          </div>
        </div>
        <div className="mt-1.5 flex flex-col gap-1.5 lg:flex-row lg:items-center lg:justify-between lg:gap-4" data-testid="detail-header">
          <RenameIdea projectId={project.id} name={project.name} compact className="pl-[5.25rem] sm:pl-[5.75rem]" onRenamed={(name) => setProject((p) => ({ ...p, name }))} />
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 lg:shrink-0" data-testid="detail-meta">
            <MaturityTag project={project} onChanged={(m, manual) => setProject((p) => ({ ...p, maturity: m, manual_maturity: manual }))} />
            {score.band && <span className="inline-flex h-6 items-center rounded-full px-2 text-[10px] font-semibold tracking-wider" style={{ color: bandColor(score.band), background: `${bandColor(score.band)}14` }} data-testid="detail-band">{bandLabel(score.band)}</span>}
            <span className="inline-flex h-6 items-center gap-1 rounded-full bg-[#f4f6fa] px-2 text-[11px] font-semibold text-[#0b0f1a] tabular-nums" data-testid="detail-reactions"><Star size={10} className="text-[#f59e0b]" /> {project.reactions_count ?? 0}</span>
            <button type="button" onClick={() => setTab("evidence")} className="inline-flex h-6 items-center gap-1 rounded-full bg-[#f4f6fa] px-2 text-[11px] font-semibold text-[#0b0f1a] tabular-nums transition-colors hover:bg-[#ede9fe]" data-testid="detail-evidence-count"><FlaskConical size={10} className="text-[#7c3aed]" /> {project.evidence_count ?? 0}</button>
            <span className="inline-flex h-6 items-center gap-1 text-[10px] text-slate-400" data-testid="detail-updated"><Clock size={10} /> {new Date(project.updated_at).toLocaleString(lang === "IT" ? "it-IT" : "en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        </div>
        </>) : (
        <div className="flex items-center gap-3" data-testid="detail-header-mini">
          <span className="shrink-0 rounded-full bg-white shadow-sm ring-1 ring-slate-200" data-testid="detail-score-mini">
            {score.total != null ? <ScoreRadial total={score.total} band={score.band} size={44} animate={false} showBand={false} /> : <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f4f6fa] text-[9px] font-semibold text-slate-400">—</span>}
          </span>
          <RenameIdea projectId={project.id} name={project.name} compact className="min-w-0 flex-1" onRenamed={(name) => setProject((p) => ({ ...p, name }))} />
          {isOwner && <span className="shrink-0 hidden sm:inline-flex" data-testid="detail-publish-mini"><PublishButton p={project} onVisibility={(v) => setProject((x) => ({ ...x, visibility: v }))} onCover={(c) => setProject((x) => ({ ...x, cover_url: c }))} onRenamed={(name) => setProject((x) => ({ ...x, name }))} /></span>}
          <span className="shrink-0" data-testid="detail-passport-mini"><PassportButton project={project} /></span>
        </div>
        )}

        {/* TABS */}
        {daysIdle >= 30 && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-[#f4f6fa] px-5 py-4 text-sm text-slate-600" data-testid="recall-banner">
            <span className="font-semibold text-[#0b0f1a]">{t("map.recall").replace("{n}", daysIdle)}</span> {t("map.recall_hint")}
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab} className={fullHeader ? "mt-3" : "mt-1"}>
          <TabsList className="hidden" data-testid="detail-tabs" aria-label={t("detail.tabs_label")}>
            {TAB_DEFS.map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className={`shrink-0 min-h-[44px] rounded-full px-4 py-2 data-[state=active]:bg-[#0b0f1a] data-[state=active]:text-white border ${k === "next" ? "border-[#7c3aed] text-[#7c3aed] font-semibold bg-[#7c3aed]/5 shadow-[0_0_0_3px_rgba(124,58,237,0.12)]" : "border-slate-200"} focus-visible:ring-2 focus-visible:ring-[#7c3aed] inline-flex items-center gap-1.5`}>
                {k === "next" && <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#7c3aed] animate-pulse" data-testid="tab-next-dot" />}
                {l}
                {SOON.includes(k) && <span className="rounded-full bg-[#7c3aed] px-1.5 py-[1px] text-[9px] font-bold tracking-wider text-white leading-none" data-testid={`tab-soon-${k}`}>{t("detail.soon")}</span>}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-5">
            <IdeaPitch project={project} canEdit={canEdit} onChanged={(pitch) => setProject((p) => ({ ...p, pitch }))} onMockups={(mockups) => setProject((p) => ({ ...p, mockups }))} />
            <IdeaDocuments project={project} canEdit={canEdit} onChanged={(documents) => setProject((p) => ({ ...p, documents }))} />
          </TabsContent>

          {/* MAPPA */}
          <TabsContent value="analysis" className="mt-4">
            <IdeaMap project={project} onOpenEvidence={(area) => openEvidence(area)} onSelect={setDecision} />
            <p className="mt-3 text-xs text-slate-500" data-testid="map-edit-hint">{lang === "IT" ? "Tocca una decisione per leggere la tua risposta e l'analisi cpenz, o per modificarla: il punteggio si ricalcola subito." : "Tap a decision to read your answer and the cpenz reading, or edit it: the score updates right away."}</p>
          </TabsContent>

          {/* SCORE */}
          <TabsContent value="score" className="mt-4">
            <React.Suspense fallback={<CPENZLoader size={80} />}><IdeaReport projectId={id} project={project} analysis={analysis} ListCmp={List} onGo={setTab} /></React.Suspense>
          </TabsContent>

          {/* NEXT MOVE */}
          <TabsContent value="next" className="mt-6">
            <div className="rounded-3xl border border-slate-200 bg-[#0b0f1a] text-white p-8" data-testid="detail-next-move">
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#a78bfa]">{t("detail.tabs.next")}</span>
              {score.cci && score.weakest_component && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-xs" data-testid="next-move-weakest">
                  <span className="uppercase tracking-wider text-slate-400 font-semibold">{t("cci.nm_weakest")}</span>
                  <span className="font-bold text-[#f9a8d4]">{t(`cci.comp.${score.weakest_component}`)} · {score.components?.[score.weakest_component]}/100</span>
                </div>
              )}
              <h2 className="mt-3 text-3xl font-bold tracking-tight">{nm.title}</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("detail.next_why")}</span>
                  <p className="mt-2 text-slate-200">{nm.why}</p>
                </div>
                {score.cci && score.weakest_component && (
                  <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4" data-testid="next-move-cci-link">
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{t("cci.nm_why")}</span>
                      <p className="text-sm text-slate-200 mt-1">{t("cci.nm_why_body", { comp: t(`cci.comp.${score.weakest_component}`), v: score.components?.[score.weakest_component] })}</p>
                    </div>
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{t("cci.nm_impact")}</span>
                      <p className="text-sm text-slate-200 mt-1">{t("cci.nm_impact_body", { comp: t(`cci.comp.${score.weakest_component}`) })}</p>
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("detail.next_do")}</span>
                  <ul className="mt-2 space-y-1.5" data-testid="next-move-checklist">
                    {(nm.what_to_do || []).map((d, i) => {
                      const done = (nm.checklist_done || []).includes(i);
                      return nm.status === "in_progress" ? (
                        <li key={i}>
                          <button type="button" onClick={() => toggleStep(i)} data-testid={`next-move-step-${i}`} aria-pressed={done} className="flex items-start gap-2 text-left text-sm w-full">
                            <span className={`mt-0.5 h-5 w-5 rounded-full border shrink-0 inline-flex items-center justify-center ${done ? "bg-[#a78bfa] border-[#a78bfa] text-[#0b0f1a]" : "border-white/30"}`}>{done && <Check size={12} />}</span>
                            <span className={done ? "line-through text-slate-400" : "text-slate-200"}>{d}</span>
                          </button>
                        </li>
                      ) : <li key={i} className="flex items-start gap-2 text-slate-200 text-sm"><Check size={14} className="text-[#a78bfa] mt-1 shrink-0" />{d}</li>;
                    })}
                  </ul>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 items-center">
                <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{t("detail.next_signal")}</span>
                  <p className="text-sm text-white mt-0.5 max-w-xs">{nm.success_signal}</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 flex items-center gap-2">
                  <Timer size={16} className="text-[#a78bfa]" />
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{t("detail.next_deadline")}</span>
                    <p className="text-sm text-white mt-0.5">{nm.deadline}</p>
                  </div>
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3 items-center" data-testid="next-move-loop">
                {nm.status === "in_progress" ? (
                  <>
                    <Button data-testid="close-next-move-btn" onClick={() => { setCloseOpen(true); setCloseResult(null); }} className="rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white px-7 h-12 text-base font-semibold shadow-[0_14px_34px_-12px_rgba(124,58,237,0.9)] focus-visible:ring-2 focus-visible:ring-white">
                      {t("cci.loop_close")} →
                    </Button>
                    <span className="inline-flex items-center rounded-full bg-[#a78bfa]/20 text-[#c4b5fd] text-xs font-semibold px-4 h-11" data-testid="next-move-in-progress">{t("cci.loop_progress")} · {(nm.checklist_done || []).length}/{(nm.what_to_do || []).length}</span>
                  </>
                ) : nm.status === "completed" ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold px-4 h-11" data-testid="next-move-completed">{t("cci.loop_closed")}</span>
                ) : (
                  <Button data-testid="start-next-move-btn" onClick={startMove} className="rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white px-7 h-12 text-base font-semibold shadow-[0_14px_34px_-12px_rgba(124,58,237,0.9)] focus-visible:ring-2 focus-visible:ring-white">
                    {t("cci.loop_start")} →
                  </Button>
                )}
                <button type="button" data-testid="plan-next-move-btn" onClick={() => addTask(nm.title)} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><CalendarDays size={13} /> {t("detail.plan_move")}</button>
              </div>
              {closeResult && (
                <div className="mt-5 rounded-2xl bg-white/10 border border-white/10 p-4" data-testid="next-move-close-result">
                  <p className="text-sm text-slate-200">{t("cci.loop_result", { area: t(`evidence.areas.${closeResult.area}`) })}</p>
                  {closeResult.cci && <p className="mt-2 text-2xl font-bold tabular-nums">{closeResult.cci.previous} → {closeResult.cci.new} <span className={`text-base ${closeResult.cci.delta >= 0 ? "text-emerald-300" : "text-orange-300"}`}>{closeResult.cci.delta >= 0 ? "+" : ""}{closeResult.cci.delta}</span></p>}
                  {closeResult.new_move && <p className="mt-2 text-sm"><span className="text-[10px] uppercase tracking-wider text-[#a78bfa] font-semibold mr-2">{t("cci.loop_new_move")}</span>{closeResult.new_move}</p>}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-400">{nm.status === "in_progress" ? t("cci.loop_hint") : t("map.loop_hint")}</p>
            </div>
            <NextMoveCoach projectId={id} initialHistory={project.next_move_chat || []} unlocked={!!project.unlocked} />
            <div className="mt-6 flex gap-3">
              <Button data-testid="add-knowledge-btn" onClick={() => { setKnowledgeOpen(true); setImpact(null); }} className="rounded-full bg-[#7c3aed] text-white px-6 h-11">
                {t("detail.update")} →
              </Button>
            </div>
          </TabsContent>

          {/* PLAN */}
          <TabsContent value="plan" className="mt-0"></TabsContent>
          {tab === "plan" && <PlanCalendar projectId={id} tasks={tasks} nextTitle={nm.title} onChanged={async () => { const tk = await api.get(`/projects/${id}/tasks`); setTasks(tk.data); }} onComplete={openComplete} />}

          {/* HISTORY */}
          <TabsContent value="evidence" className="mt-6">
            <EvidencePanel projectId={id} unlocked={!!project.unlocked} onProjectChanged={load} preset={evidencePreset} />
          </TabsContent>

          <TabsContent value="history" className="mt-6 space-y-6">
            <Timeline projectId={id} key={project.updated_at} />
          </TabsContent>

          {/* BOARD */}
          <TabsContent value="board">
            {tab === "board" && <React.Suspense fallback={<div className="mt-6 h-64 animate-pulse rounded-3xl bg-slate-100" />}><IdeaCanvas projectId={id} fill /></React.Suspense>}
          </TabsContent>

          {/* COMPARE (coming soon) */}
          {tab === "passport" && (<div className="mt-6" data-testid="detail-record"><IdeaPassport projectId={id} onGo={setTab} /></div>)}
          {tab === "accesses" && (canEdit ? <IdeaAccesses projectId={id} canEdit={canEdit} /> : <div className="mt-6 rounded-3xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="accesses-locked">{lang === "IT" ? "Gli accessi sono visibili solo al proprietario e agli editor dell'idea." : "Accesses are visible only to the idea owner and editors."}</div>)}
          {tab === "create" && <CreateWithCpenz project={project} canEdit={canEdit} onProject={load} />}
          {tab === "compare" && (<div className="mt-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center" data-testid="detail-compare-soon">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#7c3aed]/10 px-3 py-1 text-[11px] font-semibold tracking-wider uppercase text-[#7c3aed]">{t("detail.soon")}</span>
              <h3 className="mt-4 text-2xl font-bold tracking-tight text-[#0b0f1a]">{t("detail.tabs.compare")}</h3>
              <p className="mt-2 max-w-md mx-auto text-sm text-slate-500 leading-relaxed">{t("detail.compare_soon_body")}</p>
              <div className="mt-6 opacity-40 pointer-events-none select-none" aria-hidden="true"><IdeaConnections projectId={id} /></div>
            </div>
          </div>)}

          {tab === "history" && (<div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="detail-history">
            <div className="rounded-3xl border border-slate-200 p-7">
              <span className="cpenz-overline">{t("detail.score_timeline")}</span>
              {score.cci && <p className="mt-2 text-xs text-slate-400" data-testid="history-cci-note">{t("cci.history_hint")}</p>}
              <div className="mt-5 space-y-5">
                {history.map((h, i) => {
                  const prev = history[i - 1];
                  const delta = prev ? h.score - prev.score : 0;
                  const trig = h.trigger === "update" ? "knowledge" : h.trigger;
                  const by = h.change?.by_contribution || {};
                  return (
                    <div key={i} className="flex items-start gap-4" data-testid={`score-history-${h.version}`}>
                      <div className="text-2xl font-bold w-12 text-right tabular-nums" style={{ color: bandColor(h.band) }}>{h.score}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-400">v{h.version}</span>
                          {delta !== 0 && (
                            <span className={`text-xs font-semibold flex items-center gap-0.5 ${delta > 0 ? "text-green-500" : "text-orange-500"}`}>
                              {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{delta > 0 ? "+" : ""}{delta}
                            </span>
                          )}
                          {score.cci && <span className="text-[10px] font-semibold uppercase tracking-wider text-[#7c3aed]">{t(`cci.trig.${trig}`) }</span>}
                        </div>
                        <p className="text-sm text-[#0b0f1a] truncate">{h.trigger === "migration" ? t("cci.version", { v: h.calculation_version || "CCI-1.0" }) : h.trigger === "analysis" && score.cci ? t("cci.trig.analysis") : h.label}</p>
                        {score.cci && Object.keys(by).some((k) => by[k]) && (
                          <ul className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`history-components-${h.version}`}>
                            {["clarity", "evidence", "coherence", "validation"].filter((k) => by[k]).map((k) => (
                              <li key={k} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${by[k] > 0 ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>{by[k] > 0 ? "+" : ""}{by[k]} {t(`cci.comp.${k}`)}</li>
                            ))}
                          </ul>
                        )}
                        {h.reason && <p className="mt-1.5 text-xs text-slate-500 leading-relaxed" data-testid={`history-reason-${h.version}`}>{h.reason}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 p-7">
              <span className="cpenz-overline">{t("detail.versions")}</span>
              <div className="mt-5 space-y-4">
                {versions.map((v, i) => (
                  <div key={i} className="border-l-2 border-slate-100 pl-4 pb-1">
                    <span className="text-xs text-slate-400">{new Date(v.date).toLocaleDateString()}</span>
                    <p className="text-sm font-semibold text-[#0b0f1a]">{v.change}</p>
                    {v.new_information && <p className="text-sm text-slate-500 mt-1">"{v.new_information}"</p>}
                    {v.next_move && <p className="text-xs text-[#7c3aed] mt-1">→ {v.next_move}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>)}
        </Tabs>
      </div>

      {/* Knowledge dialog */}
      <Dialog open={knowledgeOpen} onOpenChange={setKnowledgeOpen}>
        <DialogContent className="sm:max-w-[520px] rounded-2xl" data-testid="knowledge-dialog">
          <DialogHeader><DialogTitle>{t("detail.update")}</DialogTitle></DialogHeader>
          <Textarea data-testid="knowledge-input" value={knowledge} onChange={(e) => setKnowledge(e.target.value)} rows={5} placeholder={t("detail.knowledge_placeholder")} className="rounded-xl" />
          {impact && (
            <div className="rounded-2xl bg-[#f4f6fa] p-4">
              <span className="text-sm font-bold text-[#7c3aed]">{impact.impact}</span>
              <p className="text-sm text-slate-600 mt-1">{impact.analysis}</p>
            </div>
          )}
          <Button data-testid="knowledge-submit" onClick={submitKnowledge} disabled={analyzing} className="rounded-full bg-[#0b0f1a] text-white h-11">
            {analyzing ? <Loader2 size={16} className="animate-spin" /> : t("detail.add_knowledge_cta")}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Add task dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-2xl" data-testid="task-dialog">
          <DialogHeader><DialogTitle>{t("detail.add_task")}</DialogTitle></DialogHeader>
          <Input data-testid="task-title-input" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder={t("detail.task_title")} className="rounded-xl" />
          <Input data-testid="task-date-input" type="date" value={taskDate} onChange={(e) => setTaskDate(e.target.value)} className="rounded-xl" />
          <Button data-testid="task-save" onClick={() => addTask()} className="rounded-full bg-[#0b0f1a] text-white h-11">{t("detail.add_task")}</Button>
        </DialogContent>
      </Dialog>

      {/* Learned dialog */}
      <Dialog open={learnOpen} onOpenChange={setLearnOpen}>
        <DialogContent className="sm:max-w-[480px] rounded-2xl" data-testid="learn-dialog">
          <DialogHeader><DialogTitle>{t("detail.learned_q")}</DialogTitle></DialogHeader>
          <Textarea data-testid="learn-input" value={learnText} onChange={(e) => setLearnText(e.target.value)} rows={4} placeholder={t("detail.knowledge_placeholder")} className="rounded-xl" />
          <Button data-testid="learn-submit" onClick={submitLearned} className="rounded-full bg-[#0b0f1a] text-white h-11">{t("detail.mark_complete")} →</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="sm:max-w-[520px] rounded-2xl" data-testid="close-move-dialog">
          <DialogHeader><DialogTitle>{t("cci.loop_learned_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{nm.title}</p>
          <div>
            <p className="text-[10px] font-semibold tracking-wider uppercase text-slate-400 mb-2">{t("cci.loop_outcome")}</p>
            <div className="flex gap-2" role="group">
              {["positive", "negative", "neutral"].map((o) => (
                <button key={o} type="button" onClick={() => setCloseOutcome(o)} aria-pressed={closeOutcome === o} data-testid={`close-outcome-${o}`}
                  className={`flex-1 h-10 rounded-full text-xs font-semibold border ${closeOutcome === o ? "bg-[#0b0f1a] text-white border-[#0b0f1a]" : "bg-white text-slate-600 border-slate-200"}`}>{t(`cci.loop_outcome_${o}`)}</button>
              ))}
            </div>
          </div>
          <Textarea data-testid="close-learned-input" value={closeText} onChange={(e) => setCloseText(e.target.value)} rows={4} placeholder={t("cci.loop_learned_ph")} className="rounded-xl" />
          <Button data-testid="close-move-submit" onClick={closeMove} disabled={closing || closeText.trim().length < 5} className="rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white h-11">
            {closing ? <Loader2 size={16} className="animate-spin" /> : `${t("cci.loop_submit")} →`}
          </Button>
        </DialogContent>
      </Dialog>

      <ShareCardDialog open={shareOpen} onOpenChange={setShareOpen} project={project} />
      <SectionDock tabs={TAB_DEFS} value={tab} onChange={setTab} soon={SOON} tools={TOOLS} hidden={!!decision || knowledgeOpen || taskOpen || learnOpen || closeOpen || shareOpen} />
      {decision && <DecisionPanel project={project} stepKey={decision} onClose={() => setDecision(null)} onProject={setProject} onGo={(k, area) => (k === "evidence" ? openEvidence(area) : setTab(k))} />}
    </div>
  );
}
